<template>
  <div :class="$s.FormContainer">
    <o-heading
      :class="$s.FormTitle"
      size="title-1"
      element="h3"
    >
      {{ pageData.form.title }}
    </o-heading>

    <o-form @submit.prevent="onSubmit">
      <o-grid>
        <o-grid-item
          :span="12"
          :tablet-landscape="6"
        >
          <o-form-item
            :errors="form.companyName.errors"
            :class="$s.FormField"
          >
            <o-input
              v-model="form.companyName.value"
              placeholder="Company Name"
              aria-label="Company Name"
            />
          </o-form-item>
        </o-grid-item>

        <o-grid-item
          :span="12"
          :tablet-landscape="6"
        >
          <o-form-item
            :errors="form.website.errors"
            :class="$s.FormField"
          >
            <o-input
              v-model="form.website.value"
              placeholder="Website"
              aria-label="Website"
            />
          </o-form-item>
        </o-grid-item>

        <o-grid-item
          :span="12"
          :tablet-landscape="6"
        >
          <o-form-item
            :errors="form.address.errors"
            :class="$s.FormField"
          >
            <o-input
              v-model="form.address.value"
              placeholder="Address"
              aria-label="Address"
            />
          </o-form-item>
        </o-grid-item>

        <o-grid-item
          :span="12"
          :tablet-landscape="3"
        >
          <o-form-item
            :class="$s.FormField"
            :errors="form.zipCode.errors"
          >
            <o-input
              v-model="form.zipCode.value"
              placeholder="Zip code"
              aria-label="Zip code"
            />
          </o-form-item>
        </o-grid-item>

        <o-grid-item
          :span="12"
          :tablet-landscape="3"
        >
          <o-form-item
            :class="$s.FormField"
            :errors="form.state.errors"
          >
            <o-input
              v-model="form.state.value"
              placeholder="State"
              aria-label="State"
            />
          </o-form-item>
        </o-grid-item>

        <o-grid-item
          :span="12"
          :tablet-landscape="6"
        >
          <o-form-item
            :class="$s.FormField"
            :errors="form.country.errors"
          >
            <o-select
              :options="countryList"
              v-model="form.country.value"
              placeholder="Country"
              aria-label="Country Select"
            />
          </o-form-item>
        </o-grid-item>

        <o-grid-item
          :span="12"
          :tablet-landscape="6"
        >
          <o-form-item
            :class="$s.FormField"
            :errors="form.email.errors"
          >
            <o-input
              v-model="form.email.value"
              placeholder="Email"
              aria-label="Email"
            />
          </o-form-item>
        </o-grid-item>

        <o-grid-item
          :span="12"
          :tablet-landscape="12"
          :class="[$s.FormField, $s.AgreeToTerms]"
        >
          <o-checkbox v-model="agreeToTerms">
            {{ pageData.form.disclaimer }}
            <kings-link
              href="//www.weebly.com/terms-of-service"
            >
              {{ pageData.form.terms }}
            </kings-link>,
            <kings-link
              href="//www.weebly.com/privacy"
            >
              {{ pageData.form.privacy }}
            </kings-link>
            <span>, and</span>
            <kings-link
              :href="termsProperties.disclaimerLink"
            >
              {{ pageData.form.termsDisclaimer }}
            </kings-link>
            <span>.</span>
          </o-checkbox>
        </o-grid-item>

        <o-grid-item
          :span="12"
          :tablet-landscape="6"
          :class="$s.FormField"
        >
          <o-button
            :full-width="true"
            :class="$s.SubmitButton"
            :disabled="!agreeToTerms"
            type="submit"
            variant="primary"
          >
            {{ pageData.form.submit }}
          </o-button>
        </o-grid-item>
      </o-grid>
    </o-form>
  </div>
</template>

<script>
import { OHeading } from 'orbit-ui/components/Heading';
import { OForm, OFormItem } from 'orbit-ui/components/Form';
import { OInput } from 'orbit-ui/components/Input';
import { OButton } from 'orbit-ui/components/Button';
import { OGrid, OGridItem } from 'orbit-ui/components/Grid';
import { OSelect } from 'orbit-ui/components/Select';
import { OCheckbox } from 'orbit-ui/components/Checkbox';

import countryList from '../utils/countryList';

export default {
  name: 'PrivacyPolicyForm',

  components: {
    OForm,
    OFormItem,
    OInput,
    OButton,
    OHeading,
    OGrid,
    OSelect,
    OCheckbox,
    OGridItem,
  },

  props: {
    pageData: {
      type: Object,
      default: () => ({}),
    },
    termsProperties: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      form: {
        companyName: {
          value: '',
          errors: [],
          validators: [
            val => (!val) && 'Company name is required',
          ],
        },
        email: {
          value: '',
          errors: [],
          validators: [
            val => (!val) && 'Email address is required',
            val => (val.indexOf('@') === -1) && 'Please enter a valid email address',
          ],
        },
        address: {
          value: '',
          errors: [],
          validators: [
            val => (!val) && 'Address is required',
          ],
        },
        zipCode: {
          value: '',
          errors: [],
          validators: [
            val => (!val) && 'Zip code is required',
          ],
        },
        country: {
          value: undefined,
          errors: [],
          validators: [
            val => (!val) && 'Country is required',
          ],
        },
        state: {
          value: '',
          errors: [],
          validators: [
            val => (!val) && 'State is required',
          ],
        },
        website: {
          value: '',
          errors: [],
          validators: [
            val => (!val) && 'Website is required',
          ],
        },
      },
      countryList,
      agreeToTerms: null,
    };
  },

  methods: {
    onSubmit() {
      if (this.validate() && this.agreeToTerms) {
        const formData = this.generateFormData();
        this.$emit('formSubmit', formData);
      }
    },

    validate() {
      this.resetErrors();

      const allErrors = Object.keys(this.form)
        .filter((formProp) => {
          const { value, errors, validators } = this.form[formProp];
          const errMsgs = validators.map(validator => validator(value)).filter(msg => msg);

          if (errMsgs.length) {
            errors.push(errMsgs[0]);
          }

          return errors.length;
        });

      return !allErrors.length;
    },

    resetErrors() {
      Object.keys(this.form).forEach((key) => {
        this.form[key].errors.splice(0);
      });
    },

    generateFormData() {
      const formData = {};

      Object.keys(this.form)
        .forEach((formProp) => {
          formData[formProp] = this.form[formProp].value;
        });

      return formData;
    },
  },
};
</script>

<style module="$s" lang="scss">
.FormContainer {
  margin: var(--space-x4) 0;

  @media (--for-tablet-landscape-up) {
    margin: unset;
  }
}

.SubmitButton {
    button {
        height: 50px;
    }
}

.FormField {
    margin-bottom: 0;
}

.FormTitle {
    margin: 0 0 var(--space-x3);

    @media (--for-tablet-landscape-up) {
        margin: var(--space-x3) 0;
    }
}

.AgreeToTerms {
  span {
      font-size: 13px;
      color: var(--color-gray-50);
  }

  a {
    text-decoration: none;
    color: var(--brand-color-dark);
  }
}
</style>
